import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import React, { useState, useEffect, useCallback, memo } from 'react'
import styled from 'styled-components'
import { SortableContainer, SortableContainerProps, SortableElement, SortableElementProps, SortableHandle } from 'react-sortable-hoc'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import { color } from 'styles/colors'
import { Circle, NoData, screenSize } from 'styles/shared'

type List = {
  id: number | string,
  tag?: string | null;
  title: string
  color?; string
}

type Props = {
  subTitle: string,
  selectedListLabel: string,
  availableListLabel: string,
  initialAvailable: Array<Partial<List>>,
  initialSelected: Array<Partial<List>>,
  onChangeCallback: (selected, available) => void
}

const DragHandle = SortableHandle(() => <S.DragHandle />)

const ListSelector = ({ subTitle, selectedListLabel, availableListLabel, initialAvailable, initialSelected, onChangeCallback }: Props) => {
  const [available, setAvailable] = useState<Array<Partial<List>>>(initialAvailable)
  const [selected, setSelected] = useState<Array<Partial<List>>>(initialSelected)

  useEffect(() => {
    setAvailable(initialAvailable)
    setSelected(initialSelected)
  }, [initialAvailable, initialSelected])

  const arrayMove = (arrayList, oldIndex, newIndex) => {
    const element = arrayList[oldIndex]
    arrayList.splice(oldIndex, 1)
    arrayList.splice(newIndex, 0, element)
    return arrayList
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const updatedSelected = arrayMove(selected, oldIndex, newIndex)
    onChangeCallback(updatedSelected, available)
  }

  const moveItemToSelected = useCallback((index) => {
    const item = available[index]
    const updatedAvailable = available.filter((_, i) => i !== index)
    const updatedSelected = [...selected, item]

    setSelected(updatedSelected)
    setAvailable(updatedAvailable)
    onChangeCallback(updatedSelected, updatedAvailable)
  }, [available, selected, onChangeCallback])

  const moveItemToAvailable = useCallback((index) => {
    const item = selected[index]
    const updatedAvailable = [...available, item]
    const updatedSelected = selected.filter((_, i) => i !== index)

    setSelected(updatedSelected)
    setAvailable(updatedAvailable)
    onChangeCallback(updatedSelected, updatedAvailable)
  }, [selected, available, onChangeCallback])

  type ItemProps = {
    value: List
    itemIndex: number
    moveItemToAvailable: (itemIndex: number) => void
  }

  type ListProps = {
    items: List[]
    moveItemToAvailable: (itemIndex: number) => void
  }

  const Item = ({ value, itemIndex, moveItemToAvailable }: ItemProps) => {
    return (
      <S.Item>
        <S.ItemDragAndName>
          <DragHandle />
          {value?.color && <Circle color={value.color} />}
          {value.title}
          {value?.tag && <S.Tag>{value.tag}</S.Tag>}
        </S.ItemDragAndName>
        <Button
          decoration="danger-secondary"
          key={value.id}
          text="Remove"
          onPress={(e) => {
            moveItemToAvailable(itemIndex)
            e.preventDefault()
            e.stopPropagation()
          }}
        />
      </S.Item>
    )
  }

  const List = ({ items, moveItemToAvailable }: ListProps) => {
    return (
      <S.Items>
        {items.map((value, index) => (
          <SortableItem
            key={`item-${value.id}`}
            index={index}
            itemIndex={index}
            value={value}
            moveItemToAvailable={moveItemToAvailable}
          />
        ))}
      </S.Items>
    )
  }

  const SortableItem = memo(SortableElement(Item)) as React.MemoExoticComponent<React.ComponentClass<unknown & SortableElementProps & ItemProps>>
  const SortableList = memo(SortableContainer(List)) as React.MemoExoticComponent<React.ComponentClass<unknown & SortableContainerProps & ListProps>>

  const filteredSelected = selected.filter(item => item !== undefined) as List[]

  return (
    <div>
      <S.Container>
        <S.List>
          <S.Label>{selectedListLabel}</S.Label>

          {filteredSelected.length > 0
            ? <SortableList items={filteredSelected} onSortEnd={onSortEnd} moveItemToAvailable={moveItemToAvailable} useDragHandle />
            : <NoData> <h2> Select</h2> <S.Subtitle> {subTitle}</S.Subtitle> </NoData>
          }
        </S.List>
        <S.Separator />
        <S.List>
          <S.Label>{availableListLabel}</S.Label>
          <S.Items>
            {available.map((item, index) => (
              <S.Item key={item.id}>
                <div>
                  {item?.color && <Circle color={item.color} />}
                  {item.title}
                  {item?.tag && <S.Tag>{item.tag}</S.Tag>}
                </div>
                <Button
                  decoration="secondary"
                  text="Select"
                  testID={`Select ${item.title}`}
                  onPress={(e) => {
                    moveItemToSelected(index)
                    e.preventDefault()
                  }}
                />
              </S.Item>
            ))}
          </S.Items>
        </S.List>
      </S.Container>
    </div>
  )
}

export default ListSelector

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    min-height: 300px;
    justify-content: space-between;
    border: 1px solid ${color('border')};
    border-radius: 5px;
    margin-top: 5px;

    @media ${screenSize.small} {
     flex-direction: column;
    }
  `,
  Label: styled.label`
    font-weight: 700;
    padding: 10px 25px;
  `,
  List: styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-height: 400px;
    width: 100%;
  `,
  Separator: styled.div`
    border: 1px solid ${color('border')};
    min-height: 300px;
    margin: 10px 0px;

    @media ${screenSize.small} {
     min-height: 0px;
      margin: 0px;
    }
  `,
  Items: styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    overflow: auto;
    height: 100%;
    min-width: 250px;
    padding: 25px;
  `,
  Item: styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
    border: 1px solid ${color('border')};
    padding: 10px 15px;
    background-color: white;
    text-wrap: wrap;
    justify-content: space-between;

    > button {
      margin-left: 20px;
    }
  `,
  Tag: styled.span`
    background-color: ${color('tagBackground')};
    color: ${color('tagText')};
    border-radius: 5px;
    margin-left: 10px;
    padding: 0 5px;
    font-weight: bold;
    cursor: default;
    border: 1px solid ${color('tagBorder')};
  `,
  ItemDragAndName: styled.div`
    display: flex;
    align-items: center;
  `,
  DragHandle: styled(DragIndicatorIcon)`
    cursor: grab;
    margin-right: 10px;
  `,
  Subtitle: styled.p`
    color: ${color('secondaryText')}
  `
}
